import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  FormControl, 
  FormControlLabel, 
  Radio, 
  RadioGroup,
  CircularProgress,
  Typography,
  Box,
  TextField,
  Divider,
  DialogTitle,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import emptyCart from '../assets/icons/empty.png';
import CartService from '../services/CartService';
import PaymentService from '../services/PaymentService';
import { Stack } from 'react-bootstrap';

const initialFormData = {
  fullname: '',
  email: '',
  phone: '',
  street1: '',
  city: '',
  state: '',
  pincode: '',
  landmark: ''
};

function Checkout() {
  const navigate = useNavigate();
  
  // State management
  const [isLoggedInChecked, setIsLoggedInChecked] = useState(false);
  const [cartData, setCartData] = useState({ data: [] });
  const [formData, setFormData] = useState(initialFormData);
  const [formDataUpdate, setFormDataUpdate] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentResponse, setPaymentResponse] = useState({});
  
  // Address management
  const [addressData, setAddressData] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [updateFormId, setUpdateFormId] = useState(null);
  
  // Promo code management
  const [promocode, setPromocode] = useState('');
  const [promocodeAllData, setPromocodeAllData] = useState({});
  const [promocodeData, setPromocodeData] = useState(null);
  const [promoApplied, setPromoApplied] = useState({});
  const [promoDiscount, setPromoDiscount] = useState(0);
  
  // Dialog management
  const [openCouponDialog, setOpenCouponDialog] = useState(false);
  const [openAddAddressDialog, setOpenAddAddressDialog] = useState(false);
  const [openUpdateAddressDialog, setOpenUpdateAddressDialog] = useState(false);

  const [formErrors, setFormErrors] = useState({
    fullname: '',
    email: '',
    phone: '',
    street1: '',
    city: '',
    state: '',
    pincode: '',
  });

  // Authentication check
  useEffect(() => {
    const isLoggedIn = JSON.parse(localStorage.getItem('isLoggedIn'));
    if (!isLoggedIn || isLoggedIn === 0) {
      navigate('/login');
    } else {
      setIsLoggedInChecked(true);
    }
  }, [navigate]);

  // Fetch cart data
  useEffect(() => {
    const fetchCartData = async () => {
      const cartServiceInstance = new CartService();
      const user_id = localStorage.getItem('userid');
      const session_key = localStorage.getItem('session_key');

      try {
        const data = await cartServiceInstance.cartlist({
          user_id,
          sessionkey: session_key,
          limit: ''
        });
        setCartData(data);
      } catch (error) {
        console.error('Error fetching cart data:', error);
      }
    };

    fetchCartData();
  }, []);

  // Fetch address data
  const fetchAddressData = useCallback(async () => {
    try {
      const user_id = localStorage.getItem('userid');
      const session_key = localStorage.getItem('session_key');
      
      const response = await fetch('https://www.liebigsagrochem.com/api/saved-address/lists/v1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, sessionkey: session_key }),
      });
      
      const data = await response.json();
      if (data.status === true) {
        setAddressData(data);
        // Set default selected address if available
        if (data.data?.length > 0) {
          const primaryAddress = data.data.find(addr => addr.is_primary === '1') || data.data[0];
          if (primaryAddress) {
            setSelectedAddressId(primaryAddress.id.toString());
          }
        }
      }
    } catch (error) {
      console.error('Error fetching address data:', error);
    }
  }, []);

  useEffect(() => {
    fetchAddressData();
  }, [fetchAddressData]);

  // Update form data when editing an address
  useEffect(() => {
    if (addressData?.data && updateFormId) {
      const address = addressData.data.find(e => e.id === updateFormId);
      if (address) {
        setFormDataUpdate({
          fullname: address.fullname || '',
          email: address.email || '',
          phone: address.mobile || '',
          street1: address.address1 || '',
          city: address.city || '',
          state: address.state || '',
          pincode: address.pincode || '',
          landmark: address.landmark || '',
        });
      }
    }
  }, [addressData, updateFormId]);

  // Calculate order totals
  const calculateTotals = useCallback(() => {
    if (!cartData.data) return { totalPrice: 0, totalDiscount: 0, totalAmount: 0 };

    const totalPrice = cartData.data.reduce((total, item) => total + parseFloat(item.total_mrp_price), 0);
    const totalDiscount = cartData.data.reduce((total, item) => total + parseFloat(item.total_discount), 0);
    const totalAmount = cartData.data.reduce((total, item) => total + parseFloat(item.final_total_price), 0);

    return { totalPrice, totalDiscount, totalAmount };
  }, [cartData]);

  const { totalPrice, totalDiscount, totalAmount } = calculateTotals();
  const finalPayableAmount = totalAmount - promoDiscount;

  // Promo code handling
  const applyPromoDiscount = useCallback(() => {
    if (!promocodeData?.status || !promocodeData?.data) return;

    const promo = promocodeData.data;
    const now = new Date();
    const startDate = new Date(promo.start_date);
    const endDate = new Date(promo.end_date);

    if (now < startDate || now > endDate) {
      setPromoApplied({ message: 'Promo code is not valid at this time.', status: false });
      return;
    }

    if (totalAmount < parseFloat(promo.min_cart_value)) {
      setPromoApplied({ message: `Minimum cart value should be ₹${promo.min_cart_value}`, status: false });
      return;
    }

    let discount = 0;
    if (promo.promo_type === 'percent') {
      discount = (totalAmount * parseFloat(promo.promo_value)) / 100;
    } else if (promo.promo_type === 'flat') {
      discount = parseFloat(promo.promo_value);
    }

    if (discount > parseFloat(promo.max_amount)) {
      discount = parseFloat(promo.max_amount);
    }

    setPromoDiscount(discount);
    setPromoApplied({ 
      message: promo.promo_type === 'percent' 
        ? `₹${discount.toFixed(2)} Discount Applied` 
        : `Flat ₹${discount.toFixed(2)} Discount Applied`, 
      status: true 
    });
  }, [promocodeData, totalAmount]);

  useEffect(() => {
    if (promocodeData?.status === true && promocodeData?.data?.promo_code === promocode.toUpperCase()) {
      applyPromoDiscount();
    }
  }, [promocodeData, promocode, applyPromoDiscount]);

  const fetchPromoDetails = async () => {
    try {
      const user_id = localStorage.getItem('userid');
      const session_key = localStorage.getItem('session_key');
      
      const response = await fetch('https://www.liebigsagrochem.com/api/promo/promo_details/v1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, promo_code: promocode, sessionkey: session_key }),
      });
      
      const data = await response.json();
      setPromocodeData(data);
    } catch (error) {
      console.error('Error fetching promocode:', error);
    }
  };

  const fetchAllPromoCodes = async () => {
    try {
      const user_id = localStorage.getItem('userid');
      const session_key = localStorage.getItem('session_key');
      
      const response = await fetch('https://www.liebigsagrochem.com/api/promo/get_promo/v1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, sessionkey: session_key }),
      });
      
      const data = await response.json();
      setPromocodeAllData(data);
    } catch (error) {
      console.error('Error fetching promocodes:', error);
    }
  };

  useEffect(() => {
    fetchAllPromoCodes();
  }, []);

  // Address CRUD operations
  const handleAddressSubmit = async (e, isUpdate = false) => {
    e.preventDefault();
    
    try {
      const user_id = localStorage.getItem('userid');
      const session_key = localStorage.getItem('session_key');
      const endpoint = isUpdate ? 'update' : 'add';
      const bodyData = isUpdate 
        ? { 
            ...formDataUpdate, 
            user_id, 
            sessionkey: session_key, 
            address_id: updateFormId,
            mobile: formDataUpdate.phone,
            address1: formDataUpdate.street1,
            address2: ''
          }
        : { 
            ...formData, 
            user_id, 
            sessionkey: session_key,
            mobile: formData.phone,
            address1: formData.street1,
            address2: ''
          };

      const response = await fetch(`https://www.liebigsagrochem.com/api/saved-address/${endpoint}/v1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
      });

      const data = await response.json();
      if (data.status === true) {
        alert(`Address ${isUpdate ? 'updated' : 'added'} successfully!`);
        if (isUpdate) {
          setOpenUpdateAddressDialog(false);
        } else {
          setOpenAddAddressDialog(false);
          setFormData(initialFormData);
        }
        await fetchAddressData();
      }
    } catch (error) {
      console.error(`Error ${isUpdate ? 'updating' : 'adding'} address:`, error);
    }
  };

  const handleSetPrimaryAddress = async (id) => {
    try {
      const user_id = localStorage.getItem('userid');
      const session_key = localStorage.getItem('session_key');
      
      const response = await fetch('https://www.liebigsagrochem.com/api/saved-address/setprimary/v1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, sessionkey: session_key, address_id: id }),
      });

      const data = await response.json();
      if (data.status === true) {
        alert('Default address changed successfully!');
        await fetchAddressData();
      }
    } catch (error) {
      console.error('Error setting primary address:', error);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      const user_id = localStorage.getItem('userid');
      const session_key = localStorage.getItem('session_key');
      
      const response = await fetch('https://www.liebigsagrochem.com/api/saved-address/remove/v1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, sessionkey: session_key, address_id: id }),
      });

      const data = await response.json();
      if (data.status === true) {
        alert('Address deleted successfully!');
        await fetchAddressData();
      }
    } catch (error) {
      console.error('Error deleting address:', error);
    }
  };

  // Payment handling
  const handlePlaceOrder = () => {
    if (!selectedAddressId) {
      alert('Please select an address');
      return;
    }

    const selectedAddress = addressData.data.find(address => address.id.toString() === selectedAddressId);
    if (!selectedAddress) {
      alert('Invalid address selected');
      return;
    }

    const cartIds = cartData.data.map(item => item.cart_id);
    const user_id = localStorage.getItem('userid');
    const session_key = localStorage.getItem('session_key');

    const orderData = {
      first_name: selectedAddress.fullname,
      email: selectedAddress.email,
      phone: selectedAddress.mobile,
      street1: selectedAddress.address1,
      street2: selectedAddress.address2 || '',
      city: selectedAddress.city,
      state: selectedAddress.state,
      pincode: selectedAddress.pincode,
      cart_id: cartIds,
      total_amount: finalPayableAmount.toFixed(2),
      user_id,
      sessionkey: session_key
    };

    processPayment(orderData);
  };

  const processPayment = async (orderData) => {
    try {
      const paymentService = new PaymentService();
      const response = await paymentService.payment_request(orderData);

      const razorpayOptions = {
        key: 'rzp_live_kw6g8k5zZTQCre',
        amount: Math.round(response.data.amount),
        currency: response.data.currency,
        order_id: response.data.id,
        name: 'Liebigs Agro chem',
        description: 'We at Liebigs are firmly.',
        image: 'https://www.liebigsagrochem.com/static/media/liebigs_logo-163x62.96f0c399e7767e48c048.webp',
        handler: handlePaymentSuccess,
        prefill: {
          name: orderData.first_name,
          email: orderData.email,
          phone: orderData.phone,
        },
        notes: {
          address: `${orderData.street1} ${orderData.street2}, ${orderData.city}, ${orderData.state}, ${orderData.pincode}`,
        },
      };

      const rzp = new window.Razorpay(razorpayOptions);
      rzp.open();
    } catch (error) {
      console.error('Payment processing failed:', error);
      alert('Payment processing failed. Please try again.');
    }
  };

  const handlePaymentSuccess = async (response) => {
    try {
      const user_id = localStorage.getItem('userid');
      const session_key = localStorage.getItem('session_key');
      
      const paymentService = new PaymentService();
      const paymentResponse = await paymentService.payment_response({
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
        user_id,
        sessionkey: session_key
      });

      setPaymentResponse(paymentResponse);
      if (paymentResponse.data.status === true) {
        setPaymentStatus('success');
        navigate('/payment-success');
      } else {
        setPaymentStatus('error');
        navigate('/payment-error');
      }
    } catch (error) {
      console.error('Error handling payment response:', error);
      alert('Error processing payment. Please contact support.');
    }
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'fullname':
        if (!value.trim()) error = 'Full name is required';
        else if (value.length < 3) error = 'Name must be at least 3 characters';
        break;
        
      case 'email':
        if (!value) error = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Invalid email format';
        break;
        
      case 'phone':
        if (!value) error = 'Phone is required';
        else if (!/^\d{10}$/.test(value)) error = 'Phone must be 10 digits';
        break;
        
      case 'street1':
        if (!value.trim()) error = 'Street address is required';
        else if (value.length < 5) error = 'Address is too short';
        break;
        
      case 'city':
        if (!value.trim()) error = 'City is required';
        break;
        
      case 'state':
        if (!value.trim()) error = 'State is required';
        break;
        
      case 'pincode':
        if (!value) error = 'Pincode is required';
        else if (!/^\d{6}$/.test(value)) error = 'Pincode must be 6 digits';
        break;
        
      default:
        break;
    }
    
    return error;
  };
  
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    Object.keys(formData).forEach(key => {
      if (key !== 'landmark') { // landmark is optional
        const error = validateField(key, formData[key]);
        newErrors[key] = error;
        if (error) isValid = false;
      }
    });
    
    setFormErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      handleAddressSubmit(e, false);
    }
  };

  // Form handling
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate the field as user types
    const error = validateField(name, value);
    
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleInputUpdate = (e) => {
    const { name, value } = e.target;
    setFormDataUpdate(prev => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (e) => {
    setSelectedAddressId(e.target.value);
  };

  // Dialog handlers
  const handleOpenUpdateAddress = (id) => {
    setUpdateFormId(id);
    setOpenUpdateAddressDialog(true);
  };

  
  

  // Validation
  /* const isValidEmail = (email) => /^\S+@\S+\.\S+$/.test(email); */

  /* const validateFormData = (data) => {
    const errors = {};
    if (!data.first_name) errors.first_name = 'First Name is required';
    if (!data.email) errors.email = 'Email is required';
    else if (!isValidEmail(data.email)) errors.email = 'Invalid email format';
    if (!data.phone) errors.phone = 'Phone Number is required';
    if (!data.street1) errors.street1 = 'Street is required';
    if (!data.city) errors.city = 'Town/City is required';
    if (!data.state) errors.state = 'State is required';
    if (!data.pincode) errors.pincode = 'Pincode is required';
    return errors;
  }; */

  // Loading state
  if (!isLoggedInChecked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Empty cart state
  if (!cartData.data?.length) {
    return (
        <div className='container py-4'>
            <Typography variant="h4" gutterBottom>Checkout</Typography>
            <div className="row">
                <div className="col-lg-7">
                    <Skeleton variant="text" width={130} height={60} />
                    <Skeleton variant="rectangular" width="100%" height={140} sx={{mb:'5px'}} />
                    <Skeleton variant="rectangular" width="100%" height={140} sx={{mb:'5px'}} />
                </div>
                <div className="col-lg-5">
                    <Skeleton variant="rectangular" width='100%' height={400} />
                </div>
            </div> 
        </div>
    );
  }

  return (
    <section className='checkout py-4'>
      <div className="container">
        <Typography variant="h4" gutterBottom>Checkout</Typography>
        
        <div className="row">
          {/* Address Section */}
          <div className="col-lg-7">
            <Box display="flex" justifyContent="space-between" alignItems="center" py={3}>
              <Typography variant="h6">Choose address</Typography>
              <Button 
                onClick={() => setOpenAddAddressDialog(true)} 
                color="success"
                sx={{ textTransform: 'capitalize' }}
              >
                + Add New Address
              </Button>
            </Box>

            {addressData?.data?.length > 0 ? (
              <FormControl fullWidth>
                <RadioGroup
                  value={selectedAddressId || ''}
                  onChange={handleRadioChange}
                >
                  {addressData.data.map(address => (
                    <Box 
                      key={address.id} 
                      border={1} 
                      borderColor="divider" 
                      borderRadius={1} 
                      p={2} 
                      mb={2}
                    >
                      <FormControlLabel
                        value={address.id.toString()}
                        control={<Radio />}
                        label={
                          <Box width="100%">
                            <Typography variant="subtitle1">{address.fullname}</Typography>
                            <Typography variant="body2">
                              {address.address1} {address.address2}, {address.city}, {address.state}, {address.pincode}
                            </Typography>
                            <Typography variant="body2">{address.mobile}</Typography>
                            <Box display="flex" justifyContent="flex-end" mt={1}>
                              <Button 
                                onClick={() => handleSetPrimaryAddress(address.id)} 
                                size="small"
                                sx={{ textTransform: 'capitalize', mr: 1 }}
                              >
                                Make default
                              </Button>
                              <Button 
                                onClick={() => handleOpenUpdateAddress(address.id)} 
                                size="small"
                                color="success"
                                sx={{ textTransform: 'capitalize', mr: 1 }}
                              >
                                Edit
                              </Button>
                              <Button 
                                onClick={() => handleDeleteAddress(address.id)} 
                                size="small"
                                color="error"
                                sx={{ textTransform: 'capitalize' }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </RadioGroup>
              </FormControl>
            ) : (
              <Box border={1} borderColor="divider" borderRadius={1} p={3}>
                <Typography variant="body1" mb={2}>No saved addresses found. Please add an address.</Typography>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={() => setOpenAddAddressDialog(true)}
                  sx={{ textTransform: 'capitalize' }}
                >
                  + Add New Address
                </Button>
              </Box>
            )}
          </div>

          {/* Order Summary Section */}
          <div className="col-lg-5">
            <Box border={1} borderColor="divider" borderRadius={1} p={3}>
              <Typography variant="h6" gutterBottom>Order Summary</Typography>
              
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Price</Typography>
                  <Typography>₹ {totalPrice.toFixed(2)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Discount</Typography>
                  <Typography color="success.main">-₹ {totalDiscount.toFixed(2)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography>Coupon savings</Typography>
                  <Button 
                    onClick={() => setOpenCouponDialog(true)} 
                    size="small"
                    color="success"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    View coupon
                  </Button>
                </Box>
                
                <Box mb={2}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter Coupon Code"
                      value={promocode}
                      onChange={(e) => setPromocode(e.target.value)}
                      sx={{ 
                        textTransform: 'uppercase',
                        mr: 1
                      }}
                    />
                    <Button 
                      onClick={fetchPromoDetails}
                      disabled={!promocode}
                      color='success'
                    variant="contained"
                      size="small"
                      sx={{backgroundColor:'#10413b', paddingY:'8px', color: 'white', textTransform: 'capitalize', ":hover": { backgroundColor: '#10413b' }}}
                    >
                      Apply
                    </Button>
                  </Box>
                  {promocodeData?.status === true ? (
                    <Typography 
                      variant="caption" 
                      color={promoApplied.status ? 'success.main' : 'error.main'}
                    >
                      {promoApplied.message}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="error.main">
                      {promocodeData?.message}
                    </Typography>
                  )}
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography fontWeight="bold">Total Amount</Typography>
                  <Typography fontWeight="bold">₹ {finalPayableAmount.toFixed(2)}</Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <div className='d-lg-flex flex-column align-items-center'>
                  <Typography mb={2} variant="body2" color="success.main">
                    You will Save ₹ {(totalDiscount + promoDiscount).toFixed(2)} in this order
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    fullWidth
                    onClick={handlePlaceOrder}
                    sx={{ textTransform: 'capitalize', backgroundColor:'#10413b', paddingY:'8px', color: 'white', ":hover": { backgroundColor: '#10413b' }}}
                  >
                    Place Order
                  </Button>
                </div>
              </Box>
            </Box>
          </div>
        </div>
      </div>

      {/* Add Address Dialog */}
      <Dialog
  open={openAddAddressDialog}
  fullWidth
  maxWidth="sm"
  onClose={() => setOpenAddAddressDialog(false)}
>
  <DialogTitle>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="h6">Add New Address</Typography>
      <IconButton onClick={() => setOpenAddAddressDialog(false)} color="error">
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>
  <DialogContent>
    <Box component="form" onSubmit={handleSubmit} mt={2}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Full Name"
            name="fullname"
            value={formData.fullname}
            onChange={handleInputChange}
            error={!!formErrors.fullname}
            helperText={formErrors.fullname}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            error={!!formErrors.email}
            helperText={formErrors.email}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            type="tel"
            inputProps={{ maxLength: 10 }}
            value={formData.phone}
            onChange={handleInputChange}
            error={!!formErrors.phone}
            helperText={formErrors.phone}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Street / Apartment / Flat"
            name="street1"
            value={formData.street1}
            onChange={handleInputChange}
            error={!!formErrors.street1}
            helperText={formErrors.street1}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="City"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            error={!!formErrors.city}
            helperText={formErrors.city}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="State"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            error={!!formErrors.state}
            helperText={formErrors.state}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Pincode"
            name="pincode"
            type="number"
            inputProps={{ maxLength: 6 }}
            value={formData.pincode}
            onChange={handleInputChange}
            error={!!formErrors.pincode}
            helperText={formErrors.pincode}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Landmark (Optional)"
            name="landmark"
            value={formData.landmark}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Button 
            type="submit" 
            variant="contained" 
            color="success" 
            fullWidth
            sx={{ mt: 2, textTransform: 'capitalize' }}
          >
            Save Address
          </Button>
        </Grid>
      </Grid>
    </Box>
  </DialogContent>
</Dialog>

      {/* Update Address Dialog */}
      <Dialog
        open={openUpdateAddressDialog}
        fullWidth
        maxWidth="sm"
        onClose={() => setOpenUpdateAddressDialog(false)}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Update Address</Typography>
            <IconButton onClick={() => setOpenUpdateAddressDialog(false)} color="error">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={(e) => handleAddressSubmit(e, true)} mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullname"
                  value={formDataUpdate.fullname}
                  onChange={handleInputUpdate}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formDataUpdate.email}
                  onChange={handleInputUpdate}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formDataUpdate.phone}
                  onChange={handleInputUpdate}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street / Apartment / Flat"
                  name="street1"
                  value={formDataUpdate.street1}
                  onChange={handleInputUpdate}
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formDataUpdate.city}
                  onChange={handleInputUpdate}
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formDataUpdate.state}
                  onChange={handleInputUpdate}
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  value={formDataUpdate.pincode}
                  onChange={handleInputUpdate}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Landmark"
                  name="landmark"
                  value={formDataUpdate.landmark}
                  onChange={handleInputUpdate}
                />
              </Grid>
              <Grid item xs={12}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="success" 
                  fullWidth
                  sx={{ mt: 2, textTransform: 'capitalize' }}
                >
                  Update Address
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Promo Codes Dialog */}
      <Dialog
        open={openCouponDialog}
        fullWidth
        maxWidth="sm"
        onClose={() => setOpenCouponDialog(false)}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Available Promo Codes</Typography>
            <IconButton onClick={() => setOpenCouponDialog(false)} color="error">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {promocodeAllData?.data?.length > 0 ? (
            <List>
              {promocodeAllData.data.map(promo => (
                <ListItem 
                  key={promo.promo_code} 
                  divider
                  secondaryAction={
                    <Button 
                      onClick={() => {
                        setOpenCouponDialog(false);
                        setPromocode(promo.promo_code);
                      }}
                      color="success"
                    >
                      Copy
                    </Button>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" color="success.main">
                        {promo.promo_code}
                      </Typography>
                    }
                    secondary={promo.description}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1">No promo codes available</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default Checkout;